import { FC, ImgHTMLAttributes, ReactNode } from 'react';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import { useImageUrl, UseImageUrlBaseProps } from '../../hooks';
import { useImagePathConfig } from '../../contexts/ImagePathContext';

const TeamShirtCVA = cva(['h-[64px] w-[64px] flex items-center justify-center'], {
  variants: {},
});

export interface TeamShirtProps extends Partial<UseImageUrlBaseProps> {
  className?: string;
  team?: string;
  /** Player position; goalkeepers auto-resolve to the competition's GK-specific kit folder, if one exists. */
  position?: string;
  assetScale?: 'auto' | '1x' | '2x';
  loading?: ImgHTMLAttributes<HTMLImageElement>['loading'];
  decoding?: ImgHTMLAttributes<HTMLImageElement>['decoding'];
  children?: ReactNode;
}

export const TeamShirt: FC<TeamShirtProps> = ({
  className,
  team,
  position,
  assetScale = '1x',
  children,
  loading = 'lazy',
  decoding = 'async',
  ext = 'png',
  hostname = 'https://engagecraftfantasydev.blob.core.windows.net',
  pathname: pathnameProp,
}) => {
  const imagePaths = useImagePathConfig();
  const normalizedTeam = team ? team.toString().trim() : '';
  const autoGkPathname =
    !pathnameProp && position === 'GK' && normalizedTeam ? imagePaths?.teamShirtGkPath : undefined;
  const pathname = pathnameProp ?? autoGkPathname ?? imagePaths?.teamShirtPath ?? 'teams/shirts';
  const fallbackValue = 'placeholder';
  const { url, value, onError } = useImageUrl({
    value: normalizedTeam || fallbackValue,
    fallbackValue,
    hostname,
    pathname,
    ext,
  });

  const imageSet = (['1x', '2x'] as const).map(scale => {
    return {
      src: `${url.toString().replace(`.${ext}`, `@${scale}.${ext}`)}`,
      scale,
    };
  });
  const srcByScale = {
    '1x': imageSet[0].src,
    '2x': imageSet[1].src,
  } as const;
  const srcSet = imageSet.map(v => `${v.src} ${v.scale}`).join(', ');
  const src = assetScale === 'auto' ? srcByScale['1x'] : srcByScale[assetScale];

  return (
    <div className={twMerge(TeamShirtCVA(), className)}>
      <img
        className="h-full aspect-auto"
        src={src}
        srcSet={assetScale === 'auto' ? srcSet : undefined}
        alt={value ?? fallbackValue}
        loading={loading}
        decoding={decoding}
        onError={onError}
      />
      {children}
    </div>
  );
};
